import { maxBy, range } from 'es-toolkit';
import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import { Line as ThreeLine } from 'three';

import { CalculatedLine, Component } from '@paragon/contract-library';
import { calculateLine } from '@paragon/relative-geometry';
import { newVector3, RenderOrder } from '@paragon/three-shared';
import { notNullish, sortBy } from '@paragon/utilities';

import {
  getReferenceLineMaterial,
  ReferenceLineStatus as ReferenceLineStatus,
} from '../../library/relativeGeometry/getReferenceLineMaterial';
import { GRID_BOUNDARIES } from '../util/designGrid';

interface ReferenceLineVisualProps {
  // The reference line to be displayed in this visual
  readonly referenceLine: CalculatedLine;

  // Needed to calculate the given reference line since it could be defined in terms of other reference lines.
  readonly component: Component;

  readonly status: ReferenceLineStatus;
  readonly renderOrder: RenderOrder;
}

export function ReferenceLineVisual({ referenceLine, component, status, renderOrder }: ReferenceLineVisualProps) {
  const lineRef = useRef<ThreeLine | null>(null);
  const { invalidate } = useThree();

  const endPoints = useMemo(() => {
    const line = calculateLine(referenceLine, component);
    if (line === null) return null;

    const intersections = GRID_BOUNDARIES.map((segment) => segment.intersectWithLine(line)).filter(notNullish);
    if (intersections.length < 2) return null;

    let endpoints = intersections;
    if (intersections.length > 2) {
      const furthestPoint = maxBy(intersections, (point) => intersections[0].distanceTo(point))!;
      const sortedIntersections = sortBy(intersections, (point) => furthestPoint.distanceTo(point));
      const largestInterval = maxBy(range(intersections.length - 1), (i) =>
        sortedIntersections[i].distanceTo(sortedIntersections[i + 1]),
      )!;
      endpoints = sortedIntersections.slice(largestInterval, largestInterval + 2);
    }

    if (endpoints[0].equals(endpoints[1])) return null;

    return endpoints;
  }, [component, referenceLine]);

  useLayoutEffect(() => {
    if (!lineRef.current || !endPoints) return;

    const vectors = endPoints.map((endPoint) => newVector3({ ...endPoint, z: 0 }));
    lineRef.current.geometry.setFromPoints(vectors);
    lineRef.current.computeLineDistances();

    invalidate();
  }, [endPoints, invalidate]);

  const referenceLineMaterial = useMemo(() => {
    return getReferenceLineMaterial(referenceLine, status);
  }, [referenceLine, status]);

  return endPoints ? (
    <threeLine ref={lineRef} renderOrder={renderOrder} material={referenceLineMaterial}>
      <bufferGeometry />
    </threeLine>
  ) : null;
}
